import { pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import ReportType from "@/core/enums/ReportTypes.enums";
import {
    BaoCaoPdfParams,
    isThuHoiVtptSheet,
    parseMonthDate,
    parseVatTuIdsQuery,
    resolveBaoCaoViewerPerPage,
} from "@/core/helpers/bao-cao-pdf-params";
import { ITaiKhoanTheoHopDongResponseBase } from "@/core/interfaces/ApiResponses";
import { BaoCaoService } from "@/core/services/BaoCao.service";
import { HopDongService } from "@/core/services/HopDong.service";
import { KhoService } from "@/core/services/Kho.service";
import BangKeChiTietNhapPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/BangKeChiTietNhapPrintTemplate.vue";
import BangKeChiTietXuatPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/BangKeChiTietXuatPrintTemplate.vue";
import GiaThanhSuaChuaCacCapPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/GiaThanhSuaChuaCacCapPrintTemplate.vue";
import HangTonKhoPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/HangTonKhoPrintTemplate.vue";
import KeHoachChiPhiPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/KeHoachChiPhiPrintTemplate.vue";
import NhapKhoVatTuPhuTungPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/NhapKhoVatTuPhuTungPrintTemplate.vue";
import NhapXuatTonKhoPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/NhapXuatTonKhoPrintTemplate.vue";
import TheoDoiHopDongPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/TheoDoiHopDongPrintTemplate.vue";
import TheoDoiVtptThuHoiPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/TheoDoiVtptThuHoiPrintTemplate.vue";
import TongHopVatTuPrintTemplate from "@/views/crafted/pages/bao-cao/print-templates/TongHopVatTuPrintTemplate.vue";
import type { Component } from "vue";

export interface BaoCaoViewerLoadOptions {
  onLoadProgress?: (loadedPage: number, totalPages: number) => void;
}

export interface BaoCaoViewerPagination {
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
}

export interface BaoCaoViewerState {
  template: Component;
  templateProps: Record<string, unknown>;
  pagination?: BaoCaoViewerPagination;
}

function monthFromDate(date: Date | null): number {
  return (date?.getMonth() ?? 0) + 1;
}

function withDonViPdfExtra(
  templateProps: Record<string, unknown>,
  extra: unknown
): Record<string, unknown> {
  const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(extra);
  return {
    ...templateProps,
    tenDonVi,
    tenDonViCha,
  };
}

async function fetchKho(khoId?: number) {
  if (!khoId) return null;

  const {
    data: { data },
  } = await KhoService.get(khoId);

  return data;
}

async function fetchHopDong(idHopDong?: number) {
  if (!idHopDong) return null;

  const {
    data: { data },
  } = await HopDongService.fetchContractDetail(idHopDong);

  return data;
}

export async function loadBaoCaoViewerData(
  params: BaoCaoPdfParams,
  options: BaoCaoViewerLoadOptions = {}
): Promise<BaoCaoViewerState> {
  switch (params.reportType) {
    case ReportType.SO_CAN_DOI_VAT_TU: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (!fromDate || !toDate || !params.khoId) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const requestedPage = params.page && params.page > 0 ? params.page : 1;
      const requestedPerPage = resolveBaoCaoViewerPerPage(params.perPage);

      const [kho, reportRes] = await Promise.all([
        fetchKho(params.khoId),
        BaoCaoService.getSoCanDoiVatTuV2({
          from: monthFromDate(fromDate),
          to: monthFromDate(toDate),
          khoId: params.khoId,
          hideZero: params.hideZero,
          page: requestedPage,
          perPage: requestedPerPage,
        }),
      ]);

      const { data: pageData, extra } = reportRes.data;
      const currentPage = pageData.current_page ?? requestedPage;
      const lastPage = pageData.last_page ?? 1;
      const perPage = Number(pageData.per_page) || requestedPerPage;
      const isLastPage = currentPage >= lastPage;

      options.onLoadProgress?.(currentPage, lastPage);

      return {
        template: HangTonKhoPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            from: fromDate,
            to: toDate,
            items: pageData.data,
            kho,
            rowStartIndex: (currentPage - 1) * perPage,
            // Grand totals belong to the final page only.
            totals: isLastPage ? extra?.totals ?? null : null,
          },
          extra
        ),
        pagination: {
          currentPage,
          lastPage,
          total: pageData.total ?? pageData.data.length,
          perPage,
        },
      };
    }

    case ReportType.BANG_KE_CHI_TIET_NHAP: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (
        !fromDate ||
        !toDate ||
        !params.khoId ||
        !params.matk ||
        !params.mact
      ) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const [kho, reportRes] = await Promise.all([
        fetchKho(params.khoId),
        BaoCaoService.getBangKeChiTietNhap({
          from: monthFromDate(fromDate),
          to: monthFromDate(toDate),
          idKho: params.khoId,
          matk: params.matk,
          mact: params.mact,
        }),
      ]);

      const taiKhoanData: ITaiKhoanTheoHopDongResponseBase = {
        ma_ct: params.mact,
        ma_tk: params.matk,
        ma_so_thue: "",
        ten_doi_tac: params.tenDoiTac ?? "",
      };

      return {
        template: BangKeChiTietNhapPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            from: fromDate,
            to: toDate,
            items: reportRes.data.data,
            kho,
            taiKhoanData,
          },
          reportRes.data.extra
        ),
      };
    }

    case ReportType.BANG_KE_CHI_TIET_XUAT: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (
        !fromDate ||
        !toDate ||
        !params.khoId ||
        !params.capSuaChuaId ||
        !params.toaXeId
      ) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const [kho, reportRes] = await Promise.all([
        fetchKho(params.khoId),
        BaoCaoService.getBangKeChiTietXuat({
          capSuaChuaId: params.capSuaChuaId,
          khoId: params.khoId,
          from: monthFromDate(fromDate),
          to: monthFromDate(toDate),
          toaXeId: params.toaXeId,
        }),
      ]);

      return {
        template: BangKeChiTietXuatPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            from: fromDate,
            to: toDate,
            items: reportRes.data.data,
            kho,
            toaXeTen: params.toaXeTen ?? "",
            toaXeMa: params.toaXeMa ?? "",
            capSuaChuaTen: params.capSuaChuaTen ?? "",
          },
          reportRes.data.extra
        ),
      };
    }

    case ReportType.TONG_HOP_NHAP_VAT_TU:
    case ReportType.TONG_HOP_XUAT_VAT_TU: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);
      const isTongHopNhap =
        params.isTongHopNhap ??
        params.reportType === ReportType.TONG_HOP_NHAP_VAT_TU;

      if (!fromDate || !toDate || !params.khoId) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const [kho, tongHop] = await Promise.all([
        fetchKho(params.khoId),
        BaoCaoService.getTongHopVatTu({
          khoId: params.khoId,
          isTongHopNhap,
          from: monthFromDate(fromDate),
          to: monthFromDate(toDate),
        }),
      ]);

      return {
        template: TongHopVatTuPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            from: fromDate,
            to: toDate,
            isTongHopNhap,
            items: tongHop.items,
            kho,
          },
          tongHop.extra
        ),
      };
    }

    case ReportType.CHI_TIET_XUAT_THEO_DOI_TUONG: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (
        !fromDate ||
        !toDate ||
        !params.khoId ||
        !params.capSuaChuaId ||
        !params.toaXeId
      ) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const [kho, reportRes] = await Promise.all([
        fetchKho(params.khoId),
        BaoCaoService.getBangKeChiTietXuatTheoDoiTuong({
          capSuaChuaId: params.capSuaChuaId,
          khoId: params.khoId,
          from: fromDate,
          to: toDate,
          toaXeId: params.toaXeId,
        }),
      ]);

      return {
        template: KeHoachChiPhiPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            from: fromDate,
            to: toDate,
            items: reportRes.data.data,
            kho,
            toaXeTen: params.toaXeTen ?? "",
            toaXeMa: params.toaXeMa ?? "",
            capSuaChuaTen: params.capSuaChuaTen ?? "",
          },
          reportRes.data.extra
        ),
      };
    }

    case ReportType.THEO_DOI_THUC_HIEN_HOP_DONG: {
      const toDate =
        parseMonthDate(params.to, params.year) ??
        (params.month && params.year
          ? new Date(params.year, params.month - 1, 1)
          : null);

      if (!toDate || !params.khoId || !params.idHopDong) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const [kho, hopDong, reportRes] = await Promise.all([
        fetchKho(params.khoId),
        fetchHopDong(params.idHopDong),
        BaoCaoService.getTheoDoiThucHienHopDong({
          idHopDong: params.idHopDong,
          idKho: params.khoId,
          month: monthFromDate(toDate),
        }),
      ]);

      return {
        template: TheoDoiHopDongPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            hopDong,
            items: reportRes.data.data,
            kho,
            to: toDate,
          },
          reportRes.data.extra
        ),
      };
    }

    case ReportType.NHAP_XUAT_TON_KHO: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (!fromDate || !toDate || !params.idDmVatTu) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const requestedPage = params.page && params.page > 0 ? params.page : 1;

      const { data: response } = await BaoCaoService.getNhapXuatTonKho({
        startMonth: monthFromDate(fromDate),
        endMonth: monthFromDate(toDate),
        idDanhMucVatTu: params.idDmVatTu,
        ...(requestedPage > 1 ? { page: requestedPage } : {}),
        search: params.search,
        hideZeroActivity: params.hideZeroActivity,
      });

      const data = response.data;

      const isPaginated = BaoCaoService.isNhapXuatTonKhoPaginated(data);
      const currentPage = isPaginated ? data.current_page ?? requestedPage : 1;
      const lastPage = isPaginated ? data.last_page ?? 1 : 1;
      const perPage = isPaginated
        ? data.per_page ?? (data.rows?.length || 1)
        : data.rows?.length || 0;
      const total = isPaginated
        ? data.total ?? data.rows?.length ?? 0
        : data.rows?.length ?? 0;

      if (isPaginated) {
        options.onLoadProgress?.(currentPage, lastPage);
      }

      return {
        template: NhapXuatTonKhoPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            items: data.rows ?? [],
            donVi: data.meta.don_vi,
            fromMonth: data.meta.start_month,
            toMonth: data.meta.end_month,
            year: data.meta.year,
            nhomVatTu: params.nhomVatTu ?? "",
          },
          response.extra
        ),
        pagination: isPaginated
          ? {
              currentPage,
              lastPage,
              total,
              perPage,
            }
          : undefined,
      };
    }

    case ReportType.NHAP_KHO_VAT_TU_PHU_TUNG: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (!fromDate || !toDate || !params.idDmVatTu) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const requestedPage = params.page && params.page > 0 ? params.page : 1;

      const { data: response } = await BaoCaoService.getNhapKhoVatTuPhuTung({
        startMonth: monthFromDate(fromDate),
        endMonth: monthFromDate(toDate),
        idDanhMucVatTu: params.idDmVatTu,
        ...(requestedPage > 1 ? { page: requestedPage } : {}),
        search: params.search,
        hideZeroActivity: params.hideZeroActivity,
      });

      const data = response.data;

      const pagination = data.meta.pagination;
      const isPaginated = BaoCaoService.isNhapKhoVatTuPhuTungPaginated(data);
      const currentPage = isPaginated
        ? pagination?.current_page ?? requestedPage
        : 1;
      const lastPage = isPaginated ? pagination?.last_page ?? 1 : 1;
      const perPage = isPaginated
        ? pagination?.per_page ?? (data.rows?.length || 1)
        : data.rows?.length || 0;
      const total = isPaginated
        ? pagination?.total ?? data.rows?.length ?? 0
        : data.rows?.length ?? 0;

      if (isPaginated) {
        options.onLoadProgress?.(currentPage, lastPage);
      }

      return {
        template: NhapKhoVatTuPhuTungPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            items: data.rows ?? [],
            donVi: data.meta.don_vi,
            fromMonth: data.meta.start_month,
            toMonth: data.meta.end_month,
            year: data.meta.year,
            nhomVatTu: params.nhomVatTu ?? "",
          },
          response.extra
        ),
        pagination: isPaginated
          ? {
              currentPage,
              lastPage,
              total,
              perPage,
            }
          : undefined,
      };
    }

    case ReportType.GIA_THANH_SUA_CHUA_CAC_CAP: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);

      if (!fromDate || !toDate || !params.idCapSc) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const requestedPage = params.page && params.page > 0 ? params.page : 1;

      const { data: response } = await BaoCaoService.getGiaThanhSuaChuaCacCap({
        startMonth: monthFromDate(fromDate),
        endMonth: monthFromDate(toDate),
        idCapSuaChua: params.idCapSc,
        ...(requestedPage > 1 ? { page: requestedPage } : {}),
        search: params.search,
      });

      const data = response.data;

      const pagination = data.meta.pagination;
      const isPaginated = BaoCaoService.isGiaThanhSuaChuaCacCapPaginated(data);
      const currentPage = isPaginated
        ? pagination?.current_page ?? requestedPage
        : 1;
      const lastPage = isPaginated ? pagination?.last_page ?? 1 : 1;
      const perPage = isPaginated
        ? pagination?.per_page ?? (data.rows?.length || 1)
        : data.rows?.length || 0;
      const total = isPaginated
        ? pagination?.total ?? data.rows?.length ?? 0
        : data.rows?.length ?? 0;

      if (isPaginated) {
        options.onLoadProgress?.(currentPage, lastPage);
      }

      return {
        template: GiaThanhSuaChuaCacCapPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            items: data.rows ?? [],
            donVi: data.meta.don_vi,
            fromMonth: data.meta.start_month,
            toMonth: data.meta.end_month,
            year: data.meta.year,
            capSuaChua: params.capSuaChuaLabel ?? "",
          },
          response.extra
        ),
        pagination: isPaginated
          ? {
              currentPage,
              lastPage,
              total,
              perPage,
            }
          : undefined,
      };
    }

    case ReportType.THEO_DOI_VTPT_THU_HOI: {
      const fromDate = parseMonthDate(params.from, params.year);
      const toDate = parseMonthDate(params.to, params.year);
      const sheet = params.sheet;

      if (!fromDate || !toDate || !params.khoId || !isThuHoiVtptSheet(sheet)) {
        throw new Error("Thiếu tham số báo cáo");
      }

      const fromMonth = monthFromDate(fromDate);
      const toMonth = monthFromDate(toDate);
      const isDetailSheet = sheet === "b01_a" || sheet === "b02_a";

      if (isDetailSheet) {
        const vatTuIds = parseVatTuIdsQuery(params.vatTuIds);
        if (!vatTuIds.length) {
          throw new Error("Thiếu danh sách vật tư cho báo cáo chi tiết");
        }

        const { data: response } = await BaoCaoService.getThuHoiVtptDetail({
          id_kho: params.khoId,
          from_month: fromMonth,
          to_month: toMonth,
          id_vat_tu: vatTuIds,
        });

        const data = response.data;

        return {
          template: TheoDoiVtptThuHoiPrintTemplate,
          templateProps: withDonViPdfExtra(
            {
              sheet,
              summary: null,
              detail: data,
              fromMonth: data.from_month,
              toMonth: data.to_month,
              year: data.year,
              tenKho: data.ten_kho,
            },
            response.extra
          ),
        };
      }

      const { data: response } = await BaoCaoService.getThuHoiVtptSummary({
        id_kho: params.khoId,
        from_month: fromMonth,
        to_month: toMonth,
      });

      const data = response.data;

      return {
        template: TheoDoiVtptThuHoiPrintTemplate,
        templateProps: withDonViPdfExtra(
          {
            sheet,
            summary: data,
            detail: null,
            fromMonth: data.from_month,
            toMonth: data.to_month,
            year: data.year,
            tenKho: data.ten_kho,
          },
          response.extra
        ),
      };
    }

    default:
      throw new Error("Loại báo cáo không được hỗ trợ");
  }
}
